import type { SquadBuilderState } from '@/lib/types/formation'
import type { PlayerRole } from '@/lib/game/types'
import { z } from 'zod'

const DRAFT_STORAGE_PREFIX = 'team_creation_shop_draft:'
const DRAFT_STORAGE_VERSION = 1

export const TEAM_CREATION_DRAFT_SCHEMA_VERSION = 1
export const TEAM_CREATION_DRAFT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000
const SCOPED_DRAFT_STORAGE_PREFIX = 'npfc:team-creation-squad-draft:v1'

export type TeamCreationDraftScope = {
  userId: string
  seasonId: string
  instanceId: string
  playMode: 'cash' | 'token'
}

export type TeamCreationSquadDraftEnvelope = TeamCreationDraftScope & {
  schemaVersion: typeof TEAM_CREATION_DRAFT_SCHEMA_VERSION
  updatedAt: string
  state: SquadBuilderState
}

interface PersistedShopDraft {
  version: number
  instanceId: string
  savedAt: string
  squadState: SquadBuilderState
}

type BrowserStorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>
type ScopedDraftStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>
type EnumerableDraftStorage = Pick<Storage, 'length' | 'key' | 'removeItem'>

const PLAYER_ROLES = ['GK', 'DEF', 'MID', 'FWD', 'MGR'] as const satisfies readonly PlayerRole[]
const playerRoleSchema = z.enum(PLAYER_ROLES)
const flexibleRoleSchema = z.enum(['DEF', 'MID', 'FWD'])

const formationPositionSchema = z
  .object({
    id: z.string(),
    position: z.enum([...PLAYER_ROLES, 'FLEXIBLE', 'FIELD']),
    x: z.number().finite(),
    y: z.number().finite(),
    required: z.boolean(),
    fillOrder: z.number().finite(),
    hybridPositions: z.array(flexibleRoleSchema).optional(),
    displayLabel: z.string().optional()
  })
  .passthrough()

const playerSchema = z
  .object({
    id: z.string().min(1),
    name: z.string().nullable().optional(),
    display_name: z.string().nullable().optional(),
    position: playerRoleSchema,
    team_name: z.string(),
    team_id: z.string().nullable().optional(),
    team_short_name: z.string().nullable().optional(),
    current_price: z.number().finite()
  })
  .passthrough()

const positionCardSchema = z
  .object({
    id: z.string(),
    position: formationPositionSchema,
    player: playerSchema.nullable(),
    isLocked: z.boolean(),
    isRequired: z.boolean(),
    canFill: z.boolean()
  })
  .passthrough()

const flexibleFormationSchema = z
  .object({
    gk: z.number().int(),
    def: z.number().int(),
    mid: z.number().int(),
    fwd: z.number().int(),
    total: z.number().int(),
    name: z.string(),
    displayName: z.string(),
    style: z.enum(['attacking', 'balanced', 'defensive', 'ultra_attacking', 'ultra_defensive']),
    isValid: z.boolean()
  })
  .passthrough()

const squadBuilderStateSchema = z
  .object({
    formation: z.string().min(1),
    formationType: z.enum(['RIGID', 'FLEXIBLE']),
    flexibleFormation: flexibleFormationSchema.optional(),
    startingEleven: z.array(positionCardSchema),
    startingManager: positionCardSchema.nullable().optional(),
    benchManager: positionCardSchema.nullable().optional(),
    reserveManager: positionCardSchema.nullable().optional(),
    substitutes: z.array(positionCardSchema),
    reserves: z.array(positionCardSchema),
    currentStep: z.enum(['GK', 'DEF', 'MGR', 'FLEXIBLE', 'SUBSTITUTES', 'COMPLETE']),
    completedPositions: z.array(z.string()),
    requiredPositionsFilled: z.boolean(),
    startingElevenComplete: z.boolean(),
    canSaveTeam: z.boolean(),
    totalCost: z.number().finite(),
    remainingBudget: z.number().finite(),
    playersSelected: z.number().int().nonnegative(),
    minPlayersRequired: z.literal(11),
    maxPlayersAllowed: z.literal(25),
    positionCounts: z.object({
      GK: z.number().int().nonnegative(),
      DEF: z.number().int().nonnegative(),
      MID: z.number().int().nonnegative(),
      FWD: z.number().int().nonnegative(),
      MGR: z.number().int().nonnegative()
    }),
    clubCounts: z.record(z.string(), z.number().int().nonnegative())
  })
  .passthrough()

const scopedDraftEnvelopeSchema = z
  .object({
    schemaVersion: z.literal(TEAM_CREATION_DRAFT_SCHEMA_VERSION),
    userId: z.string().min(1),
    seasonId: z.string().min(1),
    instanceId: z.string().min(1),
    playMode: z.enum(['cash', 'token']),
    updatedAt: z.string(),
    state: squadBuilderStateSchema
  })
  .strict()

function scopeMatches(
  candidate: TeamCreationSquadDraftEnvelope,
  scope: TeamCreationDraftScope
): boolean {
  return (
    candidate.userId === scope.userId &&
    candidate.seasonId === scope.seasonId &&
    candidate.instanceId === scope.instanceId &&
    candidate.playMode === scope.playMode
  )
}

function storageKeys(storage: Pick<Storage, 'length' | 'key'>): string[] {
  const keys: string[] = []
  for (let index = 0; index < storage.length; index += 1) {
    const key = storage.key(index)
    if (key) keys.push(key)
  }
  return keys
}

function trySetScopedDraft(
  storage: Pick<Storage, 'setItem'>,
  envelope: TeamCreationSquadDraftEnvelope
): boolean {
  try {
    storage.setItem(buildScopedTeamCreationDraftKey(envelope), JSON.stringify(envelope))
    return true
  } catch {
    return false
  }
}

export function buildScopedTeamCreationDraftKey(scope: TeamCreationDraftScope): string {
  return [
    SCOPED_DRAFT_STORAGE_PREFIX,
    encodeURIComponent(scope.userId),
    encodeURIComponent(scope.seasonId),
    encodeURIComponent(scope.instanceId),
    scope.playMode
  ].join(':')
}

export function parseScopedTeamCreationDraft(
  raw: string | null,
  scope: TeamCreationDraftScope,
  nowMs = Date.now()
): TeamCreationSquadDraftEnvelope | null {
  if (!raw) return null

  try {
    const parsed = scopedDraftEnvelopeSchema.safeParse(JSON.parse(raw))
    if (!parsed.success) return null
    const candidate = parsed.data
    if (!scopeMatches(candidate, scope)) return null

    const updatedAtMs = Date.parse(candidate.updatedAt)
    if (!Number.isFinite(updatedAtMs)) return null
    if (nowMs - updatedAtMs > TEAM_CREATION_DRAFT_MAX_AGE_MS) return null

    return candidate
  } catch {
    return null
  }
}

export function saveScopedTeamCreationDraft(
  storage: Pick<Storage, 'setItem'>,
  envelope: TeamCreationSquadDraftEnvelope
): boolean {
  return trySetScopedDraft(storage, envelope)
}

export function loadScopedTeamCreationDraft(
  storage: Pick<Storage, 'getItem' | 'removeItem'>,
  scope: TeamCreationDraftScope,
  nowMs = Date.now()
): TeamCreationSquadDraftEnvelope | null {
  const key = buildScopedTeamCreationDraftKey(scope)

  try {
    const parsed = parseScopedTeamCreationDraft(storage.getItem(key), scope, nowMs)
    if (!parsed) storage.removeItem(key)
    return parsed
  } catch {
    return null
  }
}

export function clearScopedTeamCreationDraft(
  storage: Pick<Storage, 'removeItem'>,
  scope: TeamCreationDraftScope
): void {
  try {
    storage.removeItem(buildScopedTeamCreationDraftKey(scope))
  } catch {
    // Draft persistence is best-effort when browser storage is unavailable.
  }
}

export function clearTeamCreationDraftsForUser(
  storage: EnumerableDraftStorage,
  userId: string
): void {
  const userPrefix = `${SCOPED_DRAFT_STORAGE_PREFIX}:${encodeURIComponent(userId)}:`
  try {
    for (const key of storageKeys(storage)) {
      if (key.startsWith(userPrefix)) storage.removeItem(key)
    }
  } catch {
    // Draft cleanup must not block an auth transition.
  }
}

export function clearLegacyTeamCreationDrafts(storage: EnumerableDraftStorage): void {
  try {
    for (const key of storageKeys(storage)) {
      if (key.startsWith(DRAFT_STORAGE_PREFIX)) storage.removeItem(key)
    }
  } catch {
    // Draft cleanup must not block an auth transition.
  }
}

export function migrateLegacyTeamCreationDraft(
  scope: TeamCreationDraftScope,
  storage: ScopedDraftStorage,
  nowMs = Date.now()
): TeamCreationSquadDraftEnvelope | null {
  const legacyKey = getDraftStorageKey(scope.instanceId)

  try {
    const existing = loadScopedTeamCreationDraft(storage, scope, nowMs)
    if (existing) return existing

    const rawLegacy = storage.getItem(legacyKey)
    if (!rawLegacy) return null

    const legacy: unknown = JSON.parse(rawLegacy)
    storage.removeItem(legacyKey)
    if (!isValidPersistedDraft(legacy) || legacy.instanceId !== scope.instanceId) return null

    const migrated: TeamCreationSquadDraftEnvelope = {
      schemaVersion: TEAM_CREATION_DRAFT_SCHEMA_VERSION,
      ...scope,
      updatedAt: legacy.savedAt,
      state: legacy.squadState
    }
    if (!parseScopedTeamCreationDraft(JSON.stringify(migrated), scope, nowMs)) return null
    if (!trySetScopedDraft(storage, migrated)) return null
    return migrated
  } catch {
    try {
      storage.removeItem(legacyKey)
    } catch {
      // Browser storage is unavailable; there is nothing else to migrate.
    }
    return null
  }
}

function getDraftStorage(storage?: BrowserStorageLike | null): BrowserStorageLike | null {
  if (storage) {
    return storage
  }

  if (typeof window === 'undefined') {
    return null
  }

  return window.localStorage
}

function getDraftStorageKey(instanceId: string): string {
  return `${DRAFT_STORAGE_PREFIX}${instanceId}`
}

function isValidPersistedDraft(value: unknown): value is PersistedShopDraft {
  if (!value || typeof value !== 'object') {
    return false
  }

  const candidate = value as Partial<PersistedShopDraft>

  return (
    candidate.version === DRAFT_STORAGE_VERSION &&
    typeof candidate.instanceId === 'string' &&
    candidate.instanceId.length > 0 &&
    typeof candidate.savedAt === 'string' &&
    !!candidate.squadState
  )
}

export function saveTeamCreationDraft(
  instanceId: string,
  squadState: SquadBuilderState,
  storage?: BrowserStorageLike | null
): PersistedShopDraft | null {
  const resolvedStorage = getDraftStorage(storage)
  if (!resolvedStorage) {
    return null
  }

  const payload: PersistedShopDraft = {
    version: DRAFT_STORAGE_VERSION,
    instanceId,
    savedAt: new Date().toISOString(),
    squadState
  }

  resolvedStorage.setItem(getDraftStorageKey(instanceId), JSON.stringify(payload))
  return payload
}

export function loadTeamCreationDraft(
  instanceId: string,
  storage?: BrowserStorageLike | null
): PersistedShopDraft | null {
  const resolvedStorage = getDraftStorage(storage)
  if (!resolvedStorage) {
    return null
  }

  const rawDraft = resolvedStorage.getItem(getDraftStorageKey(instanceId))
  if (!rawDraft) {
    return null
  }

  try {
    const parsed = JSON.parse(rawDraft)
    if (!isValidPersistedDraft(parsed) || parsed.instanceId !== instanceId) {
      resolvedStorage.removeItem(getDraftStorageKey(instanceId))
      return null
    }

    return parsed
  } catch {
    resolvedStorage.removeItem(getDraftStorageKey(instanceId))
    return null
  }
}

export function clearTeamCreationDraft(
  instanceId: string,
  storage?: BrowserStorageLike | null
): void {
  const resolvedStorage = getDraftStorage(storage)
  if (!resolvedStorage) {
    return
  }

  resolvedStorage.removeItem(getDraftStorageKey(instanceId))
}
